import React, {useState} from "react";
import {Button, Tooltip, message} from "antd";
import Icon from "@ant-design/icons";
import {GiFullMotorcycleHelmet} from "react-icons/gi";
import {VscRefresh} from "react-icons/vsc";
import {HiDocumentPlus} from "react-icons/hi2";
import {useObserver} from "mobx-react-lite";
import {useStore} from "hooks/useStore";
import {b64DecodeToUrl} from "utils/helpers";
import {AssignEquipmentModal} from "components/equipments/courierEquipments/modals/assignEquipmentModal/AssignEquipmentModal";
import {UploadCourierEquipmentModal} from "components/equipments/courierEquipments/modals/uploadCourierEquipmentModal/UploadCourierEquipmentModal";
import ReturnEquipmentModal from "./ReturnEquipmentModal";
import {
    INELIGIBLE_COURIER_TOOLTIP,
    hasHandoveredEquipment,
    isEquipmentEligibleCourier
} from "./courierEquipmentEligibility";

const CourierEquipmentActions = ({courier, onComplete = () => {}}) => {
    const stores = useStore();
    const [assignModalVisible, setAssignModalVisible] = useState(false);
    const [returnModalVisible, setReturnModalVisible] = useState(false);
    const [contractLoading, setContractLoading] = useState(false);

    const isEligible = isEquipmentEligibleCourier(courier);

    const canAssign = stores.meStore.checkRole("assignCourierEquipment") && isEligible;
    const canReturn =
        stores.meStore.checkRole("returnCourierEquipment") &&
        isEligible &&
        hasHandoveredEquipment(courier);
    const canCreateContract =
        stores.meStore.checkRole("createCourierEquipmentContract") && isEligible;
    const canUploadContract =
        stores.meStore.checkRole("uploadCourierEquipmentContract") && isEligible;

    // Devre dışı butonun sebebini tooltip'te göster: önce yetki, sonra kurye durumu.
    const buildTooltip = (role, noRoleText, label, extraReason) => {
        if (!stores.meStore.checkRole(role)) return noRoleText;
        if (!isEligible) return INELIGIBLE_COURIER_TOOLTIP;
        return extraReason || label;
    };

    const assignTooltip = buildTooltip(
        "assignCourierEquipment",
        "Ekipman ata yetkiniz yok",
        "Ekipman Ata"
    );
    const returnTooltip = buildTooltip(
        "returnCourierEquipment",
        "İade alma yetkiniz yok",
        "İade Al",
        !hasHandoveredEquipment(courier) ? "Hiç teslim edilmiş ekipman yok" : undefined
    );
    const createContractTooltip = buildTooltip(
        "createCourierEquipmentContract",
        "Sözleşme oluşturma yetkiniz yok",
        "Sözleşme Oluştur"
    );
    const uploadContractTooltip = buildTooltip(
        "uploadCourierEquipmentContract",
        "Yükleme yetkiniz yok",
        "Sözleşme Yükle"
    );

    const handleCreateContract = async () => {
        setContractLoading(true);
        try {
            const response = await stores.contractStore.generateContractPreview({
                courierId: courier?._id,
                contractName: "courierEquipmentAgreement"
            });
            const contract = response?.contract;
            const blobUrl = await b64DecodeToUrl(contract?.base64File);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.setAttribute("download", `${contract?.name}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            message.success("Sözleşme oluşturuldu/indirildi");
        } catch (error) {
            message.error("Sözleşme oluşturulamadı/indirilemedi");
        } finally {
            setContractLoading(false);
        }
    };

    return useObserver(() => (
        <>
            <span
                style={{display: "flex", justifyContent: "flex-end"}}
                data-testid="courierEquipmentActions"
            >
                <Tooltip title={assignTooltip}>
                    <Button
                        type="text"
                        size="small"
                        data-testid="assignEquipmentButton"
                        disabled={!canAssign}
                        onClick={() => setAssignModalVisible(true)}
                        style={{padding: "0 2px"}}
                    >
                        <Icon component={GiFullMotorcycleHelmet} style={{fontSize: 28}} />
                    </Button>
                </Tooltip>
                <Tooltip title={returnTooltip}>
                    <Button
                        type="text"
                        size="small"
                        data-testid="returnEquipmentButton"
                        disabled={!canReturn}
                        onClick={() => setReturnModalVisible(true)}
                        style={{padding: "0 2px"}}
                    >
                        <Icon
                            component={VscRefresh}
                            style={{fontSize: 24, transform: "scale(-1, 1) rotate(100deg)"}}
                        />
                    </Button>
                </Tooltip>
                <Tooltip title={createContractTooltip}>
                    <Button
                        type="text"
                        size="small"
                        data-testid="createEquipmentContractButton"
                        loading={contractLoading}
                        disabled={!canCreateContract}
                        onClick={handleCreateContract}
                        style={{padding: "0 2px"}}
                    >
                        <Icon component={HiDocumentPlus} style={{fontSize: 24}} />
                    </Button>
                </Tooltip>
                <Tooltip title={uploadContractTooltip}>
                    <div data-testid="uploadEquipmentContractButton">
                        <UploadCourierEquipmentModal
                            courierId={courier?._id}
                            disabled={!canUploadContract}
                            onSuccess={onComplete}
                        />
                    </div>
                </Tooltip>
            </span>

            <AssignEquipmentModal
                visibility={assignModalVisible}
                changeVisibility={setAssignModalVisible}
                selectedCouriers={courier ? [courier] : []}
                singleCourierMode={true}
                recall={onComplete}
                onCourierRemove={() => {}}
                onCancel={() => setAssignModalVisible(false)}
                onOk={() => {
                    setAssignModalVisible(false);
                    onComplete();
                }}
            />
            <ReturnEquipmentModal
                visible={returnModalVisible}
                courier={courier}
                onCancel={() => setReturnModalVisible(false)}
                onSuccess={onComplete}
            />
        </>
    ));
};

export default CourierEquipmentActions;
